// Queue Service
// Business logic for queue operations

import {
    addCustomerToQueue,
    getQueueDocument,
    updateQueueDocument,
    serverTimestamp
} from './firestore-service.js';
import { STATUS } from '../config/constants.js';

/**
 * Create a new customer in the queue
 */
export async function createCustomer(customerData) {
    const data = {
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        email: customerData.email,
        phone: customerData.phone,
        attentionType: customerData.attentionType,
        isPreferential: customerData.isPreferential || false,
        status: STATUS.WAITING
    };

    return await addCustomerToQueue(data);
}

/**
 * Call a customer (waiting -> calling)
 */
export async function callCustomer(customerId, calledBy, calledByName) {
    await updateQueueDocument(customerId, {
        status: STATUS.CALLING,
        calledBy: calledBy,
        calledByName: calledByName,
        wasReactivated: false
    });
}

/**
 * Start attention (calling -> serving)
 */
export async function startAttention(customerId, attendedBy) {
    await updateQueueDocument(customerId, {
        status: STATUS.SERVING,
        attendedBy: attendedBy,
        attendedAt: serverTimestamp()
    });
}

/**
 * Finish service (serving -> done)
 * Calculates attention duration automatically
 */
export async function finishService(customerId) {
    try {
        const customerDoc = await getQueueDocument(customerId);

        if (customerDoc && customerDoc.attendedAt) {
            const attendedAt = customerDoc.attendedAt.toDate();
            const finishedAt = new Date();
            const durationInSeconds = Math.round((finishedAt - attendedAt) / 1000);

            await updateQueueDocument(customerId, {
                status: STATUS.DONE,
                finishedAt: serverTimestamp(),
                attentionDurationSeconds: durationInSeconds
            });
        } else {
            // Fallback if attendedAt is missing
            await updateQueueDocument(customerId, {
                status: STATUS.DONE,
                finishedAt: serverTimestamp()
            });
        }
    } catch (error) {
        console.error("Error finishing service:", error);
        throw error;
    }
}

/**
 * Mark customer as no-show (calling -> standby)
 */
export async function markNoShow(customerId) {
    await updateQueueDocument(customerId, {
        status: STATUS.STANDBY,
        calledBy: null,
        calledByName: null
    });
}

/**
 * Reactivate a customer (standby -> waiting)
 */
export async function reactivateCustomer(customerId) {
    await updateQueueDocument(customerId, {
        status: STATUS.WAITING,
        wasReactivated: true
    });
}

/**
 * Remove preferential status from a customer
 */
export async function removePreferentialStatus(customerId) {
    await updateQueueDocument(customerId, {
        isPreferential: false
    });
}

/**
 * Submit survey feedback
 */
export async function submitSurvey(customerId, rating, comments = '') {
    await updateQueueDocument(customerId, {
        rating: parseInt(rating),
        comments: comments,
        surveyCompleted: true
    });
}

/**
 * Get customer position in waiting queue
 * Only counts customers in 'waiting' status
 */
export function getCustomerPosition(customerId, allCustomers) {
    const waitingCustomers = allCustomers.filter(c => c.status === STATUS.WAITING);
    const position = waitingCustomers.findIndex(c => c.id === customerId);
    return position >= 0 ? position + 1 : 0;
}

/**
 * Get queue counts by status
 */
export function getQueueCounts(allCustomers) {
    return {
        waiting: allCustomers.filter(c =>
            c.status === STATUS.WAITING || c.status === STATUS.CALLING
        ).length,
        standby: allCustomers.filter(c => c.status === STATUS.STANDBY).length,
        totalWaiting: allCustomers.filter(c => c.status === STATUS.WAITING).length
    };
}
